import { useCallback, useRef } from 'react';

import { useTheme } from '@mui/material';

import useBaseChartOptions from '@/components/Chart/hooks/options/useBaseChartOptions';
import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useSpotTooltipFormatter from '@/components/Chart/hooks/tooltip/useSpotTooltipFormatter';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { createLastPriceLabel } from '@/components/Chart/utils/createLastPriceLabel';
import { applySeriesVisibility } from '@/components/Chart/utils/seriesVisibility';
import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import useUserDetails from '@/hooks/useUserDetails';

import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig
} from './useChartFunctions';

import type Highcharts from 'highcharts/highstock';

const WATERMARK_NAME = '/watermark-black.svg';

const useRegularOptions = (height?: number, isDhChart = false) => {
  const createTooltipFormatter = useSpotTooltipFormatter(isDhChart ? 'Alias' : undefined);
  const theme = useTheme();
  const { tooltip } = getRegularTooltip(createTooltipFormatter, theme);
  const { isUserBasic, isUserExpired } = useUserDetails();
  const isUserExpiredValue = Boolean(isUserExpired);

  const { createBaseChartOptions } = useBaseChartOptions(
    height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT
  );

  const { createEventOptions } = useChartFunctions();
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
  const { aspectRatio } = getWatermarkConfig(WATERMARK_NAME);

  const getWatermarkAttributes = (chart: Highcharts.Chart) => {
    return getStandardWatermarkAttributes(chart, aspectRatio, isUserBasic, isUserExpiredValue);
  };

  const { events, exportEvents } = createEventOptions(
    watermarkImage,
    getWatermarkAttributes,
    WATERMARK_NAME
  );
  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      const visibleSeries = applySeriesVisibility(chartSeries, hiddenSeriesIds) ?? chartSeries;
      let options = createBaseChartOptions(
        visibleSeries,
        theme.palette.text.primary,
        theme.palette.background.paper
      );
      options = { ...options, chart: { ...options.chart, events } };
      options = {
        ...options,
        plotOptions: {
          ...options.plotOptions,
          series: {
            ...options.plotOptions?.series,
            dataLabels: {
              position: 'right',
              enabled: true,
              useHTML: true,
              formatter(): string | undefined {
                return createLastPriceLabel(this, theme.palette.background.default);
              }
            }
          }
        },
        exporting: {
          ...options.exporting,
          chartOptions: {
            ...options.exporting?.chartOptions,
            chart: { events: exportEvents },
            plotOptions: { series: { dataLabels: { enabled: false } } }
          },
          sourceWidth: undefined,
          sourceHeight: undefined,
          buttons: undefined
        }
      };
      options = {
        ...options,
        stockTools: {
          gui: {
            enabled: false
          }
        }
      };

      options = { ...options, tooltip };
      options = { ...options, series: visibleSeries };
      return options;
    },
    [
      createBaseChartOptions,
      theme.palette.background.default,
      theme.palette.background.paper,
      theme.palette.text.primary,
      tooltip,
      events,
      exportEvents
    ]
  );

  return { createOptions };
};

export default useRegularOptions;
